import { useState, useEffect } from 'react'
import { NAV_LINKS }  from '../constants/data'
import { useMFEStore } from '../mfe/hooks'
import MFEBus         from '../mfe/MFEBus'
import MFEStore       from '../mfe/MFEStore'

/**
 * NavBar — floating capsule navigation.
 *
 * • Shrinks slightly after 40px of scroll
 * • Active section highlighted via IntersectionObserver
 * • Emits nav:section on the MFEBus + syncs to MFEStore
 * • Theme toggle button (☀ / ☾) persisted via ThemeProvider
 * • scrollTo() waits 380ms after mobile-menu close before
 *   calling scrollIntoView, preventing the overlay race condition
 */
export function NavBar() {
  const [open,   setOpen]   = useState(false)
  const [active, setActive] = useState('home')
  const [shrink, setShrink] = useState(false)
  const [store,  setStore]  = useMFEStore()

  const isDark = store.theme !== 'light'

  useEffect(() => {
    const io = new IntersectionObserver(
      es => es.forEach(e => {
        if (e.isIntersecting) {
          setActive(e.target.id)
          MFEBus.emit('nav:section', { id: e.target.id })
          MFEStore.set({ activeSection: e.target.id })
        }
      }),
      { rootMargin: '-40% 0px -55% 0px' }
    )
    document.querySelectorAll('section[id],main[id]').forEach(s => io.observe(s))
    const onScroll = () => setShrink(window.scrollY > 40)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => { io.disconnect(); window.removeEventListener('scroll', onScroll) }
  }, [])

  const scrollTo = id => e => {
    e.preventDefault()
    if (open) {
      setOpen(false)
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 380)
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const toggleTheme = () => setStore({ theme: isDark ? 'light' : 'dark' })

  return (<>
    <nav className={shrink ? 'shrink' : ''}>
      <a href="#home" className="nl" onClick={scrollTo('home')}>DP</a>

      <ul className="nm">
        {NAV_LINKS.map(([id, lbl]) => (
          <li key={id}>
            <a href={`#${id}`} className={active === id ? 'active' : ''} onClick={scrollTo(id)}>
              {lbl}
            </a>
          </li>
        ))}
      </ul>

      {/* theme toggle */}
      <button className="theme-btn" onClick={toggleTheme} aria-label="Toggle light / dark mode" title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
        {isDark ? '☀' : '☾'}
      </button>

      <a href="#contact" className="nh-btn" onClick={scrollTo('contact')}>Hire Me →</a>

      <button className={`hbg${open ? ' open' : ''}`} onClick={() => setOpen(o => !o)} aria-label="Menu">
        <span/><span/><span/>
      </button>
    </nav>

    <div className={`mob-menu${open ? ' open' : ''}`}>
      {NAV_LINKS.map(([id, lbl]) => (
        <a key={id} href={`#${id}`} onClick={scrollTo(id)}>{lbl}</a>
      ))}
      <button className="theme-btn" onClick={toggleTheme} style={{ marginTop: '1rem' }} aria-label="Toggle theme">
        {isDark ? '☀ Light mode' : '☾ Dark mode'}
      </button>
    </div>
  </>)
}
